import React, { useEffect, useState, useCallback } from 'react';
import { auth } from '../../firebase-config';
import { onAuthStateChanged, signInWithPopup, GoogleAuthProvider, signOut } from 'firebase/auth';
import { useWorkerRegistration } from './useWorkerRegistration';
import { useJobLoop } from './useJobLoop';

/**
 * Muuzbox Phone Worker
 *
 * Standalone PWA route (#worker) that turns a phone into a YouTube-download
 * worker. When the desktop Muuzbox can't fetch a video locally (its IP is
 * YT-flagged), it creates a doc in `youtubeDownloadJobs/{jobId}`. The
 * Cloud Function `notifyWorkersOfNewJob` pushes an FCM notification to
 * every registered worker's device — including phones with the screen
 * off. The user taps the notification, this page opens, the job is
 * claimed via a Firestore transaction, the video is processed, and the
 * resulting MP3 is uploaded to Hetzner. The desktop pulls from there.
 *
 * Auth: gated by email allow-list (same list used by the admin pages).
 * Anyone else gets a polite "not authorised" screen.
 */

const ADMIN_EMAILS = [
  'godbody@gmail.com',
  'thisisobah@gmail.com',
  'oba@muuzbox.com',
];

const isAdminEmail = (email) => !!email && ADMIN_EMAILS.includes(email.toLowerCase());

const WorkerApp = () => {
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [signInErr, setSignInErr] = useState('');

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u || null);
      setAuthReady(true);
    });
    return () => unsub();
  }, []);

  const handleSignIn = useCallback(async () => {
    setSignInErr('');
    try {
      await signInWithPopup(auth, new GoogleAuthProvider());
    } catch (e) {
      setSignInErr(e.message || String(e));
    }
  }, []);

  const handleSignOut = useCallback(async () => {
    try { await signOut(auth); } catch (_) {}
  }, []);

  if (!authReady) return <Screen>Loading…</Screen>;

  if (!user) {
    return (
      <Screen>
        <h1 style={s.h1}>Muuzbox Worker</h1>
        <p style={s.muted}>Sign in to enable this device as a download worker.</p>
        <button style={s.btn} onClick={handleSignIn}>Sign in with Google</button>
        {signInErr && <p style={s.err}>{signInErr}</p>}
      </Screen>
    );
  }

  if (!isAdminEmail(user.email)) {
    return (
      <Screen>
        <h1 style={s.h1}>Muuzbox Worker</h1>
        <p style={s.err}>
          <strong>{user.email}</strong> isn't authorised to be a worker.
        </p>
        <button style={s.btnGhost} onClick={handleSignOut}>Sign out</button>
      </Screen>
    );
  }

  return <WorkerLoggedIn user={user} onSignOut={handleSignOut} />;
};

const WorkerLoggedIn = ({ user, onSignOut }) => {
  const reg = useWorkerRegistration(user);
  const loop = useJobLoop({ workerId: reg.workerId, ownerUid: user.uid });

  return (
    <Screen>
      <header style={s.header}>
        <div>
          <h1 style={s.h1}>Muuzbox Worker</h1>
          <div style={s.muted}>{user.email}</div>
        </div>
        <button style={s.btnGhost} onClick={onSignOut}>Sign out</button>
      </header>

      <StatusCard
        label="Device registration"
        value={reg.deviceLabel}
        ok={!!reg.workerId}
        detail={reg.workerId ? `id: ${reg.workerId}` : reg.error || 'starting…'}
      />

      <StatusCard
        label="Heartbeat"
        value={reg.lastHeartbeatAt ? `last beat ${formatRelative(reg.lastHeartbeatAt)}` : 'pending…'}
        ok={reg.heartbeatActive}
        detail={reg.heartbeatActive ? 'beating every 30s' : 'not yet beating'}
      />

      <StatusCard
        label="Push notifications (FCM)"
        value={reg.fcmStatus}
        ok={reg.fcmToken !== null && reg.fcmStatus !== 'denied' && reg.fcmStatus !== 'error'}
        detail={reg.fcmToken ? `token registered (${reg.fcmToken.slice(0, 12)}…)` : reg.fcmError || 'requesting permission…'}
        action={reg.fcmStatus === 'denied' ? {
          label: 'Open browser settings to allow notifications',
          onClick: () => alert('Open this page in your browser settings → Permissions → Notifications, then reload.'),
        } : null}
      />

      <StatusCard
        label="Job loop"
        value={loop.state}
        ok={loop.state === 'idle' || loop.state === 'processing'}
        detail={loop.detail}
      />

      {loop.currentJob && <JobCard job={loop.currentJob} progress={loop.progress} />}

      <RecentJobsList jobs={loop.recentJobs} />

      <footer style={s.footer}>
        <p style={s.muted}>
          Keep this tab in the foreground for active processing.
          Push notifications wake the tab when new jobs arrive.
        </p>
      </footer>
    </Screen>
  );
};

const StatusCard = ({ label, value, ok, detail, action }) => (
  <div style={{ ...s.card, borderLeft: `4px solid ${ok ? '#22c55e' : '#eab308'}` }}>
    <div style={s.cardLabel}>{label}</div>
    <div style={s.cardValue}>{value || '—'}</div>
    {detail && <div style={s.cardDetail}>{detail}</div>}
    {action && <button style={s.btnSmall} onClick={action.onClick}>{action.label}</button>}
  </div>
);

const JobCard = ({ job, progress }) => (
  <div style={{ ...s.card, borderLeft: '4px solid #06b6d4' }}>
    <div style={s.cardLabel}>Now processing</div>
    <div style={s.cardValue}>{job.title || job.videoId}</div>
    <div style={s.cardDetail}>{job.artist || ''}</div>
    {progress && (
      <div style={s.progressBarWrap}>
        <div style={{ ...s.progressBarFill, width: `${Math.round(progress.percent || 0)}%` }} />
      </div>
    )}
    {progress?.message && <div style={s.cardDetail}>{progress.message}</div>}
  </div>
);

const RecentJobsList = ({ jobs }) => (
  <div style={s.card}>
    <div style={s.cardLabel}>Recent jobs</div>
    {(!jobs || jobs.length === 0) ? (
      <div style={s.cardDetail}>No jobs processed yet.</div>
    ) : (
      <ul style={s.list}>
        {jobs.map((j) => (
          <li key={j.id} style={s.listItem}>
            <span>{j.title || j.videoId}</span>
            <span style={{ color: j.status === 'ready' ? '#22c55e' : j.status === 'failed' ? '#ef4444' : '#94a3b8' }}>
              {j.status}
            </span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

const Screen = ({ children }) => (
  <div style={s.screen}>
    <div style={s.inner}>{children}</div>
  </div>
);

const formatRelative = (ms) => {
  const dt = Date.now() - ms;
  if (dt < 60000) return `${Math.round(dt / 1000)}s ago`;
  if (dt < 3600000) return `${Math.round(dt / 60000)}m ago`;
  return new Date(ms).toLocaleTimeString();
};

const s = {
  screen: { minHeight: '100vh', background: 'linear-gradient(180deg, #0f172a 0%, #020617 100%)', color: 'white', padding: 16, fontFamily: 'system-ui, -apple-system, sans-serif' },
  inner: { maxWidth: 480, margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, gap: 12 },
  h1: { fontSize: 24, fontWeight: 700, margin: 0 },
  muted: { color: '#94a3b8', fontSize: 13 },
  err: { color: '#fca5a5', fontSize: 14, marginTop: 8 },
  card: { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderLeft: '4px solid rgba(255,255,255,0.1)', borderRadius: 12, padding: 16, marginBottom: 12 },
  cardLabel: { fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: '#94a3b8', marginBottom: 4 },
  cardValue: { fontSize: 16, fontWeight: 600 },
  cardDetail: { fontSize: 13, color: '#94a3b8', marginTop: 4, wordBreak: 'break-word' },
  btn: { background: '#06b6d4', color: 'white', border: 0, padding: '12px 20px', borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: 'pointer', marginTop: 12 },
  btnGhost: { background: 'transparent', color: '#94a3b8', border: '1px solid rgba(255,255,255,0.2)', padding: '8px 14px', borderRadius: 8, fontSize: 13, cursor: 'pointer' },
  btnSmall: { background: 'rgba(255,255,255,0.1)', color: 'white', border: 0, padding: '6px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer', marginTop: 8 },
  footer: { marginTop: 20, textAlign: 'center' },
  progressBarWrap: { height: 6, background: 'rgba(255,255,255,0.1)', borderRadius: 3, overflow: 'hidden', marginTop: 8 },
  progressBarFill: { height: '100%', background: '#06b6d4', transition: 'width 0.3s ease' },
  list: { listStyle: 'none', padding: 0, margin: '8px 0 0' },
  listItem: { display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.05)', fontSize: 13 },
};

export default WorkerApp;
